import { AnyAction } from 'redux'
import { getLogger } from 'shared/logger'
import { ContentService, ContentQuery } from 'services/ContentService'
import { buildContentState, buildFacetState } from 'client/store/buildContentState'
import { loadMoreCountSelector, currentPageInfoSelector } from 'client/store/paging/selectors'
import { refinementIdsByFilterSelector } from 'client/store/refinement/selectors'
import { categoryByIdSelector } from 'client/store/filterCategories'
import { updateUrl } from 'client/store/locationState/utils'
import {
  getGalleryNameSelector,
  getLocaleSelector,
  getMpvid,
  getQuantitySelector,
  selectedOptionsSelector,
  getDpcId,
  getDesignConceptId,
} from 'client/store/config'
import {
  getBypassApproval,
  getNoCache,
  getUseConstraints,
  getForcedRankingStrategyOverride,
  getIsDirectQuickView,
  getTemplatePurposes,
  getRawSets,
} from 'client/store/debug'
import { CONTENT_REVERT, CONTENT_UPDATE } from 'client/store/constants'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { getRawExperiments } from 'client/store/experimentation'
import { v4 } from 'uuid'
import {
  getRenderPropsOverrides,
  getSearchBackend,
  getDesignCreationTypes,
  getASPExperimentFlags,
  getDebugModeEnabled,
  getQuickViewId,
} from 'client/store/debug/reducer'
import { booleanRenderPropertySelector, getProductKey } from 'client/store/config/reducer'
import { RenderProperty } from 'shared/renderProperties'
import { isNotFalse } from '~/shared/heplers'
import { assembleASPExperimentFlags } from 'shared/experiments/ASPSearch/assembleASPExperimentFlags'
import { getMetadataQueryParams } from 'client/store/content/utils'
import { INITIAL_STATE as DEFAULT_PAGE_STATE } from 'client/store/paging/constants'
import { getContentQueryConfigExperimentFlags } from 'shared/experiments/utils'
import { designPersonalizationContextIdSelector, imageAppliedSelector } from 'client/store/designPersonalization/selectors'
import { DesignPersonalizationContextService } from '~/services/DesignPersonalizationContextService'
import { normalizeTemplateInteractionsToBoosts } from '~/client/utils/normalizeTemplateInteractionsToBoosts'
import { isExperimentActive } from 'shared/ab-testing'
import { BOOST_VARIATIONS, BoostingASPExperiments, RELEVANT_BOOST_TYPES } from 'shared/experiments/DesignBoost/constants'
import { PREVIOUSLY_ORDERED_DAYS_OF_HISTORY, TEMPLATE_INTERACTION_SUBTYPE, TEMPLATE_INTERACTION_TYPE } from 'client/constants'
import { ASPNewnessExperimentNewnessPercentage } from 'shared/experiments/ASPSearch/ASPNewnessExperiment'
import { FirstImpressionSetExploration, FirstImpressionSetName } from 'shared/experiments/ASPSearch/FirstImpressionExperiment'
import {
  IMAGE_PLACEHOLDER_BOOST_KEY,
  IMAGE_PLACEHOLDER_BOOST_WEIGHT,
  PersonalizationBoostingExperimentFlags,
} from 'shared/experiments/Personalization/constants'
import { taxonomyIdByCategoryIdSelector } from 'client/store/filterCategories/reducer'
import { boostsSelector } from 'client/store/boosts/reducer'
import { updateBoosts } from 'client/store/boosts/actions'
import { authDataSelector } from 'client/store/auth/reducer'
import { updateLatestTemplateInteraction } from 'client/store/previouslyOrdered/actions'
import { combineWithPrioritySets } from '~/shared/sets/combineWithPrioritySets'
import { IS_BROWSER } from 'client/constants'
import { createPreviouslyOrderedSet } from '~/shared/sets/previouslyOrderedTemplate'
import { ThunkDispatch } from 'client/store/interface'
import { AuthInfo } from 'server/utils/types'
import { createDesignPersonalizationSet } from '~/shared/sets/designPersonalizationTemplate'

/**
 * Gets personalized set from query params in state
 * @param state
 * @returns Gallery.Models.Url.JSONObject | undefined - The personalized set configuration
 */
const getDesignPersonalizationSet = (state: State.GlobalState): Gallery.Models.Url.JSONObject | undefined => {
  const dpcId = getDpcId(state)
  const designConceptId = getDesignConceptId(state)

  if (!dpcId || !designConceptId) {
    return undefined
  }

  return createDesignPersonalizationSet(designConceptId, dpcId)
}

/**
 * Redux action to get Previously Ordered set configuration
 * @param resolvedCanonicalId - The canonical ID from auth
 * @param resolvedAccessToken - The access token from auth
 * @returns Promise<Gallery.Models.Url.JSONObject | undefined> - The previously ordered set configuration
 */
export const getPreviouslyOrderedSet =
  (resolvedCanonicalId: string, resolvedAccessToken: string) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<Gallery.Models.Url.JSONObject | undefined> => {
    const state = getState()

    try {
      const templateInteractions = await DesignPersonalizationContextService.getTemplateInteractions(
        resolvedCanonicalId,
        resolvedAccessToken,
        [TEMPLATE_INTERACTION_TYPE.TEMPLATE_ORDERED],
        [TEMPLATE_INTERACTION_SUBTYPE.TEMPLATE_TOKEN],
        PREVIOUSLY_ORDERED_DAYS_OF_HISTORY,
      )

      if (!templateInteractions.length) {
        return
      }

      const latestTemplate = templateInteractions.at(-1)

      if (!latestTemplate) {
        return
      }

      dispatch(updateLatestTemplateInteraction(latestTemplate))

      const productKey = getProductKey(state)
      const locale = getLocaleSelector(state)
      const matchingTemplates = await ContentService.getMatchingTemplatesForProduct(productKey, locale, decodeURIComponent(latestTemplate.data))

      if (!matchingTemplates.length) {
        return
      }

      const dpcId = designPersonalizationContextIdSelector(state)
      const { designConceptId } = matchingTemplates[0]

      return createPreviouslyOrderedSet(designConceptId, dpcId)
    } catch (error) {
      getLogger().error({
        message: 'Failed to get previously ordered template',
        error: error as Error,
        canonicalId: resolvedCanonicalId,
      })

      return
    }
  }

/**
 * Redux action to get all sets for the content query including previously ordered sets
 * @param resolvedCanonicalId - The canonical ID from auth
 * @param resolvedAccessToken - The access token from auth
 * @returns Promise<Gallery.Models.Url.JSONObject[]> - Combined sets array
 */
const getSets =
  (resolvedCanonicalId: string | undefined, resolvedAccessToken: string | undefined) =>
  async (dispatch: ThunkDispatch, getState: () => State.GlobalState): Promise<Gallery.Models.Url.JSONObject[]> => {
    const sets = getRawSets(getState()) || []
    let previouslyOrderedSet: Gallery.Models.Url.JSONObject | undefined

    // Get personalized set from query params in state
    const personalizedSet = getDesignPersonalizationSet(getState())

    if (resolvedAccessToken && resolvedCanonicalId) {
      previouslyOrderedSet = await dispatch(getPreviouslyOrderedSet(resolvedCanonicalId, resolvedAccessToken))
    }

    return combineWithPrioritySets(sets, previouslyOrderedSet, personalizedSet)
  }

type ContentCategories = {
  categories: string[][]
  facetCategories: string[]
}

type GetContentCategoriesFn = (selectedCategories: Util.StringDictionary<string[]>) => ContentCategories

/**
 * Gets categories to filter and facet on
 *
 * Prune L1s out of request when filtering on L2s to get the correct results back
 *   ex: (L1,L2) in the QS would behave like (L1 OR L2) and return all L1 content plus L2, which isn't correct
 * @param state
 */
export function getContentCategories(state: State.GlobalState): GetContentCategoriesFn {
  const getCategory = categoryByIdSelector(state)

  return (selectedCategories: Util.StringDictionary<string[]>): ContentCategories => {
    const parentIds = {} as Util.StringDictionary<string>

    const roots = Object.values(state.filters.byId)
      .filter((f) => f.dimension === REFINEMENT_DIMENSION.CATEGORY)
      .map((f) => (f as State.CategoryFilter).categoryId.toString())

    // iterate over the categories for that filter,
    //   finding the max depth and then filtering on that depth
    // exclude L2s from facetCategories since we dont show L3s
    const categories = Object.values(selectedCategories).map((catIds) => {
      let maxDepth = 0

      catIds.forEach((catId) => {
        const category = getCategory(catId)

        if (!category) {
          throw new Error(`Invalid category. Category ${catId} does not exist`)
        }

        const { level } = category

        if (level > maxDepth) {
          maxDepth = level
        }

        // L3s will never be shown, so no facets needed
        if (level < 2) {
          parentIds[catId] = catId
        }
      })

      return catIds.filter((catId) => getCategory(catId).level === maxDepth)
    })

    return {
      categories,
      facetCategories: roots.concat(Object.keys(parentIds)),
    }
  }
}

interface ContentUpdateParams {
  newContent: Gallery.ContentQuery.Response
  locale: i18n.Locale
  query: ContentQuery
  quantity: Gallery.Models.Url.ValidParsedQsValue<number>
  qvContent?: Gallery.ContentQuery.TileEntityResponse
  oldState?: State.GlobalState
  getTaxonomyIdByCategoryId: (categoryId: string) => string
  selectedTaxonomyIds: string[]
}

function generateContentUpdate({
  newContent,
  locale,
  query,
  quantity,
  qvContent,
  oldState,
  getTaxonomyIdByCategoryId,
  selectedTaxonomyIds,
}: ContentUpdateParams): AnyAction {
  const loadMoreCount = oldState ? oldState.paging.loadMoreCount + 1 : DEFAULT_PAGE_STATE.loadMoreCount

  return {
    type: CONTENT_UPDATE,
    payload: {
      ...buildFacetState(newContent.results.facets),
      ...buildContentState({
        content: newContent.results.content,
        locale,
        quantity,
        getTaxonomyIdByCategoryId,
        qvContent,
        selectedTaxonomyIds,
        oldState,
      }),
      paging: {
        totalEntities: newContent.total,
        loadMoreCount,
        hasMoreTemplates: newContent.length + newContent.offset < newContent.total,
      },
      requestUrl: newContent.requestUrl,
      config: {
        viewId: v4(),
        lastLoadMoreLength: newContent.results.content.length,
      },
    },
    meta: {
      query,
    },
  }
}

function generateContentRevert(originalState: State.GlobalState, query: ContentQuery): AnyAction {
  return {
    type: CONTENT_REVERT,
    payload: {
      paging: originalState.paging,
      refinements: originalState.refinements,
    },
    meta: {
      query,
    },
  }
}

interface ContentUpdateOptions {
  actions?: AnyAction | AnyAction[]
  shouldUpdateUrl?: boolean
  loadMore?: boolean
  callback?: () => void
  shouldUpdateBoosts?: boolean
  latestOrderedTemplate?: VP.DesignPersonalization.Models.DesignPersonalizationContextService.TemplateInteraction
  authInfo?: AuthInfo
}

/**
 * Action Creator to update the content state.
 * Given a query to perform against the Content Service, retrieve the new
 * content and return the CONTENT_UPDATE action.
 */
export const contentUpdate =
  ({
    actions,
    authInfo: { canonicalId, accessToken } = {},
    shouldUpdateUrl = true,
    loadMore = false,
    shouldUpdateBoosts = false,
  }: ContentUpdateOptions = {}): Gallery.ContentQuery.Action =>
  async (dispatch, getState): Promise<AnyAction> => {
    const originalState = getState()
    const rawExperiments = getRawExperiments(originalState)
    const booleanRenderProperty = booleanRenderPropertySelector(originalState)
    const showPersonalizationUI = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
    const PersonalizationBoostingDisabled = booleanRenderProperty(RenderProperty.PersonalizationBoostingDisabled)

    if (actions) {
      ;[actions].flat().forEach(dispatch)
    }

    const state = getState()
    const { keywords: metadataKeywords, categories: metadataCategories, attributes: metadataAttributes } = getMetadataQueryParams(state)
    const refinements = refinementIdsByFilterSelector(state)
    const getTaxonomyIdByCategoryId = taxonomyIdByCategoryIdSelector(state)
    const { categories, facetCategories } = getContentCategories(state)({
      ...refinements.categories,
      ...(metadataCategories?.length && { meta: metadataCategories.map(String) }),
    })
    const quantity = getQuantitySelector(state)
    const designId = getQuickViewId(state)
    const isDirectQuickView = getIsDirectQuickView(state)
    const loadMoreCount = loadMoreCountSelector(state)

    const searchBackend = getSearchBackend(state)
    const debugEnabled = getDebugModeEnabled(state)
    const designCreationTypes = getDesignCreationTypes(state)
    const designPersonalizationContextId = designPersonalizationContextIdSelector(state)
    const imagePersonalized = imageAppliedSelector(state)
    const authData = authDataSelector(state)

    const boostedSearchableEnumeratedData: Gallery.ContentQuery.BoostedSearchableEnumeratedData[] = []
    let rolledOutExperimentFlags: Gallery.Models.Url.ASPExperimentFlags = {}

    // accessToken and canonicalId are only passed during server-side rendering
    const resolvedAccessToken = accessToken ?? authData?.accessToken
    const resolvedCanonicalId = canonicalId ?? authData?.canonicalId

    if (showPersonalizationUI && imagePersonalized && !PersonalizationBoostingDisabled) {
      rolledOutExperimentFlags = PersonalizationBoostingExperimentFlags
      boostedSearchableEnumeratedData.push({
        key: IMAGE_PLACEHOLDER_BOOST_KEY,
        value: 'true',
        weight: IMAGE_PLACEHOLDER_BOOST_WEIGHT,
      })
    }

    const experimentFlags = assembleASPExperimentFlags(
      [ASPNewnessExperimentNewnessPercentage, ...BoostingASPExperiments, FirstImpressionSetExploration, FirstImpressionSetName],
      rawExperiments,
      getASPExperimentFlags(state),
      rolledOutExperimentFlags,
    )
    const configExperimentFlags = getContentQueryConfigExperimentFlags(rawExperiments)

    const { pageSize, offset } = currentPageInfoSelector(state, loadMore ? loadMoreCount + 1 : DEFAULT_PAGE_STATE.loadMoreCount)

    const attributes = [...metadataAttributes, ...Object.values(refinements.attributes)]

    const boostEnabled = isExperimentActive(BOOST_VARIATIONS.Enabled, rawExperiments)

    if (boostEnabled && shouldUpdateBoosts && resolvedCanonicalId && resolvedAccessToken) {
      const templateInteractions = await DesignPersonalizationContextService.getTemplateInteractions(
        resolvedCanonicalId,
        resolvedAccessToken,
        RELEVANT_BOOST_TYPES,
      )

      const { boostedKeywords, boostedCategories } = normalizeTemplateInteractionsToBoosts(templateInteractions)

      dispatch(
        updateBoosts({
          boostedKeywords,
          boostedCategories,
        }),
      )
    }

    const boosts = boostEnabled ? boostsSelector(state) : { boostedKeywords: [], boostedCategories: [] }

    const sets = await dispatch(getSets(resolvedCanonicalId, resolvedAccessToken))
    const query: ContentQuery = {
      categories,
      facetCategories,
      attributes,
      bypassApproval: getBypassApproval(state),
      locale: getLocaleSelector(state),
      galleryName: getGalleryNameSelector(state),
      keyword: [...metadataKeywords, refinements.keyword].filter(isNotFalse),
      collection: refinements.collection,
      length: pageSize,
      mpvId: getMpvid(state),
      offset,
      selectedOptions: selectedOptionsSelector(state),
      noCache: getNoCache(state),
      useConstraints: getUseConstraints(state),
      forcedRankingStrategyOverride: getForcedRankingStrategyOverride(state),
      renderProps: getRenderPropsOverrides(state),
      templatePurposes: getTemplatePurposes(state),
      searchBackend,
      experimentFlags,
      configExperimentFlags,
      designCreationTypes,
      designPersonalizationContextId,
      debug: debugEnabled,
      boostedKeywords: boosts.boostedKeywords,
      boostedCategories: boosts.boostedCategories,
      boostedSearchableEnumeratedData,
      includeCategoryAndKeywordData: true,
      filterAltTextCompatibleTagging: true,
      sets,
    }

    try {
      const newContent = await ContentService.getContent(query)

      let qvContent

      if (isDirectQuickView && designId) {
        const qvQuery: ContentQuery = {
          bypassApproval: getBypassApproval(state),
          locale: getLocaleSelector(state),
          galleryName: getGalleryNameSelector(state),
          mpvId: getMpvid(state),
          noCache: getNoCache(state),
          useConstraints: getUseConstraints(state),
          forcedRankingStrategyOverride: getForcedRankingStrategyOverride(state),
          designIds: [designId],
          length: 1,
          selectedOptions: selectedOptionsSelector(state),
          attributes: Object.values(refinements.attributes),
          renderProps: getRenderPropsOverrides(state),
          searchBackend,
          experimentFlags,
          configExperimentFlags,
          debug: debugEnabled,
          designCreationTypes,
          includeCategoryAndKeywordData: true,
          sets,
        }

        ;[qvContent] = (await ContentService.getContent(qvQuery)).results.content

        if (!qvContent) {
          const message = `Failed to open quickView for QSP: ${designId}`

          getLogger().warn({
            message,
            query,
          })
        }
      }

      let contentUpdateAction: AnyAction = generateContentUpdate({
        newContent,
        locale: state.config.locale,
        query,
        quantity,
        qvContent,
        oldState: loadMore ? state : undefined,
        getTaxonomyIdByCategoryId,
        selectedTaxonomyIds: categories.flat(),
      })

      if (shouldUpdateUrl) {
        contentUpdateAction = updateUrl(contentUpdateAction)
      }

      return dispatch(contentUpdateAction)
    } catch (e) {
      // revert on the client, otherwise throw
      if (IS_BROWSER) {
        getLogger().error(e as Error)
        return dispatch(generateContentRevert(originalState, query))
      }
      throw e
    }
  }
